import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { initAnalytics, trackPageview } from './services/analytics';
import { AuthProvider, useAuth } from './context/auth-context';
import SidebarNav from './components/sidebar-nav';
import ProtectedRoute from './components/protected-route';
import { ROUTE_TIERS } from './config/tier-config';
import LandingPage from './pages/landing-page';
import LoginPage from './pages/login-page';
import DashboardPage from './pages/dashboard-page';
import CoinDetailPage from './pages/coin-detail-page';
import WatchlistPage from './pages/watchlist-page';
import RubberbandPage from './pages/rubberband-page';
import RsiMtfPage from './pages/rsi-mtf-page';
import CciPullbackPage from './pages/cci-pullback-page';
import Mtf50smaPage from './pages/mtf-50sma-page';
import TrendPullbackPage from './pages/trend-pullback-page';
import TopDogPage from './pages/topdog-page';
import TopDogCciCombinedPage from './pages/topdog-cci-combined-page';
import TwentyFiftyPullbackPage from './pages/twenty-fifty-pullback-page';
import TwentyEmaSrPage from './pages/twenty-ema-sr-page';
import EmaParabolicPage from './pages/ema-parabolic-page';
import VolumeSpikePage from './pages/volume-spike-page';
import TrendBreakoutPage from './pages/trend-breakout-page';
import TtmSqueezePage from './pages/ttm-squeeze-page';
import TtmTripleSqueezePage from './pages/ttm-triple-squeeze-page';
import MaCrossoverPage from './pages/ma-crossover-page';
import KcExtremePage from './pages/kc-extreme-page';
import CombinedPage from './pages/combined-page';
import AlertsLogPage from './pages/alerts-log-page';
import SettingsPage from './pages/settings-page';
import PricingPage from './pages/pricing-page';
import SupportPage from './pages/support-page';
import './App.css';

// Inner component so we can use useAuth (which needs AuthProvider above it)
function AppRoutes() {
  const { user, isLoading } = useAuth();
  const location = useLocation();

  useEffect(() => { initAnalytics(); }, []);
  useEffect(() => { trackPageview(location.pathname); }, [location.pathname]);

  if (isLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', color: 'rgba(255,255,255,0.6)' }}>
        Loading…
      </div>
    );
  }

  // Pages that show without the sidebar (public pages)
  const isPublicRoute = ['/', '/login'].includes(location.pathname);

  return (
    <div className="App">
      {user && !isPublicRoute && <SidebarNav />}

      <main className={user && !isPublicRoute ? 'main-content' : 'main-content-full'}>
        <Routes>
          {/* Public */}
          <Route path="/"      element={user ? <Navigate to="/dashboard" replace /> : <LandingPage />} />
          <Route path="/login" element={user ? <Navigate to="/dashboard" replace /> : <LoginPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/support" element={<SupportPage />} />

          {/* Free tier */}
          <Route path="/dashboard" element={<ProtectedRoute element={<DashboardPage />}  requiredTier={ROUTE_TIERS['/dashboard']} />} />
          <Route path="/coin/:id"  element={<ProtectedRoute element={<CoinDetailPage />} requiredTier={ROUTE_TIERS['/coin/:id']} />} />
          <Route path="/watchlist" element={<ProtectedRoute element={<WatchlistPage />}  requiredTier={ROUTE_TIERS['/watchlist']} />} />
          <Route path="/alerts-log" element={<ProtectedRoute element={<AlertsLogPage />} requiredTier={ROUTE_TIERS['/alerts-log']} />} />
          <Route path="/settings"  element={<ProtectedRoute element={<SettingsPage />}   requiredTier={ROUTE_TIERS['/settings']} />} />

          {/* Pro tier */}
          <Route path="/rubberband" element={<ProtectedRoute element={<RubberbandPage />} requiredTier={ROUTE_TIERS['/rubberband']} />} />

          {/* Elite tier */}
          <Route path="/20-50-pullback"   element={<ProtectedRoute element={<TwentyFiftyPullbackPage />}    requiredTier={ROUTE_TIERS['/20-50-pullback']} />} />
          <Route path="/rsi-mtf"         element={<ProtectedRoute element={<RsiMtfPage />}                 requiredTier={ROUTE_TIERS['/rsi-mtf']} />} />
          <Route path="/cci-pullback"    element={<ProtectedRoute element={<CciPullbackPage />}            requiredTier={ROUTE_TIERS['/cci-pullback']} />} />
          <Route path="/mtf-50sma"       element={<ProtectedRoute element={<Mtf50smaPage />}               requiredTier={ROUTE_TIERS['/mtf-50sma']} />} />
          <Route path="/topdog"          element={<ProtectedRoute element={<TopDogPage />}                 requiredTier={ROUTE_TIERS['/topdog']} />} />
          <Route path="/topdog-cci-combined" element={<ProtectedRoute element={<TopDogCciCombinedPage />}  requiredTier={ROUTE_TIERS['/topdog-cci-combined']} />} />
          <Route path="/trend-pullback"  element={<ProtectedRoute element={<TrendPullbackPage />}          requiredTier={ROUTE_TIERS['/trend-pullback']} />} />
          <Route path="/20ema-sr"        element={<ProtectedRoute element={<TwentyEmaSrPage />}            requiredTier={ROUTE_TIERS['/20ema-sr']} />} />
          <Route path="/20-8ema-parabolic" element={<ProtectedRoute element={<EmaParabolicPage />}         requiredTier={ROUTE_TIERS['/20-8ema-parabolic']} />} />
          <Route path="/volume-spike"    element={<ProtectedRoute element={<VolumeSpikePage />}            requiredTier={ROUTE_TIERS['/volume-spike']} />} />
          <Route path="/trend-breakout"  element={<ProtectedRoute element={<TrendBreakoutPage />}          requiredTier={ROUTE_TIERS['/trend-breakout']} />} />
          <Route path="/ttm-squeeze"     element={<ProtectedRoute element={<TtmSqueezePage />}             requiredTier={ROUTE_TIERS['/ttm-squeeze']} />} />
          <Route path="/ttm-triple-squeeze" element={<ProtectedRoute element={<TtmTripleSqueezePage />}    requiredTier={ROUTE_TIERS['/ttm-triple-squeeze']} />} />
          <Route path="/ma-crossover"    element={<ProtectedRoute element={<MaCrossoverPage />}            requiredTier={ROUTE_TIERS['/ma-crossover']} />} />
          <Route path="/kc-extreme"      element={<ProtectedRoute element={<KcExtremePage />}              requiredTier={ROUTE_TIERS['/kc-extreme']} />} />
          <Route path="/combined"      element={<ProtectedRoute element={<CombinedPage />}               requiredTier={ROUTE_TIERS['/combined']} />} />

          {/* Fallback */}
          <Route path="*" element={<Navigate to={user ? '/dashboard' : '/'} replace />} />
        </Routes>
      </main>
    </div>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </Router>
  );
}

export default App;
